import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { initialCustomerState } from 'store/customer/initial'
import { getNextStep, getPreviousStep, getSpreadedFlowsWithNewValues } from 'store/customer/utils'
import { CustomerFlows, InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import {
  AccountDictionary,
  CustomerStore,
  GoToStepPayload,
  ItsmePrefilledKeys,
  NextStepPayload,
  PreviousStepPayload,
  SelectPropositionPayload,
  UpdateAllInputsPayload,
  UpdateFixedPricesPayload,
  UpdateFlowAlternativePayload,
  UpdateGroupInputsPayload,
  UpdateInputPayload,
  UpdatePropositionsPayload
} from 'store/customer/types'
import { runSimulation } from 'store/customer/thunks'
import { Product, SimulationType } from '@boltenergy-be/shared'
import { PortalAccount } from 'lib/partners/types'

export const CustomerSlice = createSlice({
  initialState: initialCustomerState,
  name: 'customer',
  reducers: {
    goToStep: (state, { payload }: PayloadAction<GoToStepPayload>) => {
      const { flow, step } = payload

      state.flows = getSpreadedFlowsWithNewValues(state.flows, flow, {
        currentStep: step
      })
    },
    nextStep: (state, { payload }: PayloadAction<NextStepPayload>) => {
      const { flow } = payload

      const { currentStep, steps } = state.flows[flow]
      state.flows = getSpreadedFlowsWithNewValues(state.flows, flow, {
        currentStep: getNextStep(steps, currentStep)
      })
    },
    previousStep: (state, { payload }: PayloadAction<PreviousStepPayload>) => {
      const { flow } = payload

      const { currentStep, steps } = state.flows[flow]
      state.flows = getSpreadedFlowsWithNewValues(state.flows, flow, {
        currentStep: getPreviousStep(steps, currentStep)
      })
    },
    resetCustomerStore: (_, { payload }: PayloadAction<SimulationType>) => {
      return {
        ...initialCustomerState,
        inputs: {
          ...initialCustomerState.inputs,
          [InputGroupKeys.SIMULATION]: {
            ...initialCustomerState.inputs[InputGroupKeys.SIMULATION],
            [SimulationFieldKeys.CHOSEN_SIMULATION_TYPE]: payload
          }
        }
      }
    },
    selectProposition: (state, { payload }: PayloadAction<SelectPropositionPayload>) => {
      const { productType } = payload
      state.propositions.selectedProposition = productType
    },
    setCreditCheckRequired: (state, { payload }: PayloadAction<boolean>) => {
      state.flows = getSpreadedFlowsWithNewValues(state.flows, CustomerFlows.REGISTRATION, {
        creditCheckRequired: payload
      })
    },
    setDidSimulation: (state) => {
      state.flows = getSpreadedFlowsWithNewValues(state.flows, CustomerFlows.SIMULATION, {
        didSimulation: true
      })
    },
    setShowPlentyPropositions: (state, { payload }: PayloadAction<boolean>) => {
      state.propositions.showPlentyPropositions = payload
    },
    setFlowAlternative: (state, { payload }: PayloadAction<UpdateFlowAlternativePayload>) => {
      state.flowAlternative[payload.key] = payload.value
    },
    setProductForSpecificSimulation: (state, { payload }: PayloadAction<Product>) => {
      state.propositions.simulateSpecificProduct = payload
    },
    setStartedFunnelFromPath: (state, { payload }: PayloadAction<string>) => {
      state.startedFunnelFromPath = payload
    },
    toggleContactMe: (state, { payload }: PayloadAction<Product | undefined>) => {
      state.flows = getSpreadedFlowsWithNewValues(state.flows, CustomerFlows.SIMULATION, {
        contactMe: payload
      })
    },
    updateAllInputs: (state, { payload }: PayloadAction<UpdateAllInputsPayload>) => {
      state.inputs = payload.inputs
    },
    updateGroupInputs: <Group extends InputGroupKeys>(state, { payload }: PayloadAction<UpdateGroupInputsPayload<Group>>) => {
      state.inputs[payload.group] = payload.inputs
    },
    updateFixedPrices: (state, { payload }: PayloadAction<UpdateFixedPricesPayload>) => {
      state.fixedPrices = payload
    },
    updateInput: (state, { payload }: PayloadAction<UpdateInputPayload>) => {
      const { group, key, value } = payload
      state.inputs[group][key] = value
    },
    updateSimulatedPropositions: (state, { payload }: PayloadAction<UpdatePropositionsPayload>) => {
      const { propositions } = payload
      state.propositions.simulatedPropositions = propositions
    },
    setIsExtraAddressFlow: (state, { payload }: PayloadAction<boolean>) => {
      state.extraAddress.isExtraAddressFlow = payload
    },
    setSelectedExtraAddressAccount: (state, { payload }: PayloadAction<string | undefined>) => {
      state.extraAddress.selectedAccount = payload
    },
    setExtraAddressAccountsByEmail: (state, { payload }: PayloadAction<{ email: string; accounts: PortalAccount[] }>) => {
      const { email, accounts } = payload

      // normalize accounts into a dictionary
      const accountsDict: AccountDictionary = {}
      accounts.forEach((account) => {
        accountsDict[account.customerNumber] = account
      })

      if (!state.extraAddress?.accounts) {
        state.extraAddress.accounts = {}
      }

      state.extraAddress.accounts[email] = accountsDict
    },
    setItsmePrefilled: (state, { payload }: PayloadAction<CustomerStore['itsme']['prefilled']>) => {
      const prefilled = payload

      if (!state.itsme) {
        state.itsme = { prefilled }
      } else {
        state.itsme.prefilled = prefilled
      }
    },
    setItsmeOverwrittenByKey: (state, { payload }: PayloadAction<{ key: keyof CustomerStore['itsme']['overwritten']; value: boolean }>) => {
      const { key, value } = payload

      if (!state.itsme?.overwritten) {
        state.itsme = {
          ...state.itsme,
          overwritten: {}
        }
      }

      state.itsme.overwritten[key] = value
    },
    resetItsmePrefilled: (state) => {
      if (state.itsme?.prefilled) {
        state.itsme.prefilled = undefined
      }
    }
  },
  selectors: {
    selectSimulationLoading: (state) => state.flows[CustomerFlows.SIMULATION].isLoading,
    selectExtraAddress: (state, email: string) =>
      email && state.extraAddress.selectedAccount
        ? state.extraAddress?.accounts?.[email]?.[state.extraAddress?.selectedAccount]
        : undefined,
    selectItsmeKey: (state, key: ItsmePrefilledKeys, parent: keyof CustomerStore['itsme']) => state?.itsme?.[parent]?.[key],
    selectInputGroup: (state, group: InputGroupKeys) => state.inputs[group],
    selectSelectedProposition: (state) => state.propositions.selectedProposition
  },
  extraReducers: (builder) => {
    // PENDING THUNKS
    builder.addCase(runSimulation.pending, (state) => {
      state.flows = getSpreadedFlowsWithNewValues(state.flows, CustomerFlows.SIMULATION, {
        error: false,
        isLoading: true
      })
    })

    // SUCCESSFUL THUNKS
    builder.addCase(runSimulation.fulfilled, (state, { payload: propositions }) => {
      state.flows = getSpreadedFlowsWithNewValues(state.flows, CustomerFlows.SIMULATION, {
        error: false,
        isLoading: false
      })
      state.propositions.simulatedPropositions = {
        ...state.propositions.simulatedPropositions,
        ...propositions
      }
    })

    // REJECTED THUNKS
    builder.addCase(runSimulation.rejected, (state) => {
      state.flows = getSpreadedFlowsWithNewValues(state.flows, CustomerFlows.SIMULATION, {
        error: true,
        isLoading: false
      })
    })
  }
})

export const {
  goToStep,
  nextStep,
  previousStep,
  resetCustomerStore,
  selectProposition,
  setCreditCheckRequired,
  setDidSimulation,
  setExtraAddressAccountsByEmail,
  setFlowAlternative,
  setIsExtraAddressFlow,
  setItsmePrefilled,
  setItsmeOverwrittenByKey,
  resetItsmePrefilled,
  setProductForSpecificSimulation,
  setSelectedExtraAddressAccount,
  setShowPlentyPropositions,
  setStartedFunnelFromPath,
  toggleContactMe,
  updateAllInputs,
  updateFixedPrices,
  updateInput,
  updateGroupInputs,
  updateSimulatedPropositions
} = CustomerSlice.actions

export const { selectSimulationLoading, selectExtraAddress, selectItsmeKey, selectInputGroup, selectSelectedProposition } =
  CustomerSlice.selectors

export default CustomerSlice.reducer
